<script setup lang="ts">
import { usePermissionsStore } from '@/modules/IdentityAndAccess/permissionsStore'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { toast } from '@/shared/toast'
import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'
import IconButton from '@/uiKit/IconButton.vue'
import Menu from '@/uiKit/Menu'
import { ref } from 'vue'
import { useFolderMembersStore } from './useFolderMembers'
import { useFolderStore } from './useFolderStore'

const props = defineProps<{
  id: string
  canCreateSubfolder: boolean
}>()

defineEmits<{
  'create:subfolder': []
  'create:agent': []
  rename: []
  'manage-permissions': []
}>()

const folderStore = useFolderStore()

const { workspaceId } = useWorkspaceContext()

const showDeleteDialog = ref(false)
const onConfirmDelete = async () => {
  showDeleteDialog.value = false
  const res = await folderStore.deleteFolder({
    folderId: props.id,
    workspaceId: workspaceId.value,
  })

  if (!res.ok && res.error.status !== 404) {
    toast.error('Failed to delete folder. Please contact support if the problem persists.')
  }
}

const permissionsStore = usePermissionsStore()
const folderMembersStore = useFolderMembersStore()
const onOpenChange = (isOpen: boolean) => {
  if (!isOpen) {
    return
  }

  /**
   * Here, we eagerly load the folder members so that there is a minimal (ideally 0)
   * delay between the user clicking the "Manage Permissions" option, and the folder
   * permissions dialog opening and rendering all the folder members.
   */
  folderMembersStore.loadFolderMembers({
    folderId: props.id,
    workspaceId: workspaceId.value,
  })
}

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <Menu.Root
    v-slot="{ getTriggerProps, menu }"
    close-on-select
    @change:open="onOpenChange"
  >
    <IconButton
      icon="more-dots"
      size="sm"
      rounded
      variant="transparent"
      v-bind="{ ...getTriggerProps(), ...$attrs }"
      class="aspect-square"
      :class="[menu.open && 'opacity-100']"
      @click.stop
    />
    <Menu.Content class="min-w-56">
      <Menu.Item
        v-if="canCreateSubfolder"
        icon="folder"
        label="Create subfolder"
        @select="$emit('create:subfolder')"
      />
      <Menu.Item
        v-if="permissionsStore.workspacePermissions.create_projects"
        label="New agent"
        icon="circle-plus"
        @select="$emit('create:agent')"
      />
      <Menu.Item
        icon="edit"
        label="Rename"
        @select="$emit('rename')"
      />
      <Menu.Item
        icon="settings"
        label="Manage permissions"
        @select="$emit('manage-permissions')"
      />
      <Menu.Divider />
      <Menu.Item
        icon="trash"
        label="Delete folder"
        critical
        @select="showDeleteDialog = true"
      />
    </Menu.Content>
  </Menu.Root>
  <ConfirmationDialog
    :open="showDeleteDialog"
    title="Delete this folder?"
    description="This folder will be deleted immediately. You can't undo this action."
    @confirm="onConfirmDelete"
    @close="showDeleteDialog = false"
  />
</template>
